/***************************************************************************

    geoSchema.ts
    -------------------------
    last Update          : October 23, 2026
    copyright            : (C) 2025 by CIL4Sys Engineering

***************************************************************************/

import { PageSeoLang } from "./pageSeo"
import { getResourceArticle } from "./resourceArticles"

const SAME_AS = [
	"https://www.linkedin.com/company/cil4sys-engineering",
	"https://x.com/CIL4Sys",
]

export type GeoFaqItem = { q: string; a: string }

/** FAQ visible + schema: resources articles only (not official Strapi pages). */
export function getPageFaqs(aPageKey: string, aLang: PageSeoLang): Array<GeoFaqItem> {
	if (aPageKey.startsWith("resources/")) {
		const slug = aPageKey.slice("resources/".length)
		return getResourceArticle(slug)?.faqs[aLang] || []
	}
	return []
}

function faqPageSchema(someFaqs: Array<GeoFaqItem>): Record<string, unknown> {
	return {
		"@context": "https://schema.org",
		"@type": "FAQPage",
		mainEntity: someFaqs.map((aFaq) => ({
			"@type": "Question",
			name: aFaq.q,
			acceptedAnswer: {
				"@type": "Answer",
				text: aFaq.a,
			},
		})),
	}
}

function softwareApplicationSchema(aSiteUrl: string): Array<Record<string, unknown>> {
	const org = {
		"@type": "Organization",
		name: "CIL4Sys Engineering",
		url: aSiteUrl,
	}
	return [
		{
			"@context": "https://schema.org",
			"@type": "SoftwareApplication",
			name: "Sim4Sys Designer",
			applicationCategory: "BusinessApplication",
			operatingSystem: "Cross-platform (MBSE host tools)",
			description:
				"MBSE add-ons for Papyrus, IBM Rhapsody, PTC Modeler, and CATIA Magic that guide modeling and generate requirements, specifications, and executable images.",
			url: `${aSiteUrl}/solutions/`,
			publisher: org,
		},
		{
			"@context": "https://schema.org",
			"@type": "SoftwareApplication",
			name: "Sim4Sys Virtual Bench",
			applicationCategory: "BusinessApplication",
			operatingSystem: "Web",
			description:
				"SaaS functional digital mock-up for collaborative simulation, scenario-based testing, and automated verification reports.",
			url: `${aSiteUrl}/solutions/`,
			publisher: org,
		},
		{
			"@context": "https://schema.org",
			"@type": "SoftwareApplication",
			name: "SyneSys",
			applicationCategory: "BusinessApplication",
			operatingSystem: "Web",
			description:
				"AI companion for systems engineering that converts documents into structured models under human-in-the-loop control, with SysML v2 export.",
			url: "https://www.synesys.fr/",
			publisher: org,
		},
	]
}

function breadcrumbSchema(aPageKey: string, aLang: PageSeoLang, aBaseUrl: string): Record<string, unknown> | null {
	if (aPageKey === "home") {
		return null
	}
	const labels: Record<string, Record<PageSeoLang, string>> = {
		services: { en: "Services", fr: "Services" },
		solutions: { en: "Solutions", fr: "Solutions" },
		formation: { en: "Training", fr: "Formations" },
		usecases: { en: "Use cases", fr: "Cas d'usage" },
		about_us: { en: "About", fr: "À propos" },
		"contact-us": { en: "Contact", fr: "Contact" },
		resources: { en: "Resources", fr: "Ressources" },
	}
	const langPrefix = aLang === "fr" ? "/fr" : ""
	if (aPageKey.startsWith("resources/")) {
		const slug = aPageKey.slice("resources/".length)
		const article = getResourceArticle(slug)
		if (!article) {
			return null
		}
		return {
			"@context": "https://schema.org",
			"@type": "BreadcrumbList",
			itemListElement: [
				{ "@type": "ListItem", position: 1, name: "CIL4Sys", item: `${aBaseUrl}${langPrefix}/` },
				{
					"@type": "ListItem",
					position: 2,
					name: labels.resources[aLang],
					item: `${aBaseUrl}${langPrefix}/resources/`,
				},
				{
					"@type": "ListItem",
					position: 3,
					name: article.h1[aLang],
					item: `${aBaseUrl}${langPrefix}/resources/${slug}/`,
				},
			],
		}
	}
	const label = labels[aPageKey]?.[aLang]
	if (!label) {
		return null
	}
	const pagePath = `/${aPageKey}/`
	return {
		"@context": "https://schema.org",
		"@type": "BreadcrumbList",
		itemListElement: [
			{
				"@type": "ListItem",
				position: 1,
				name: "CIL4Sys",
				item: `${aBaseUrl}${langPrefix}/`,
			},
			{
				"@type": "ListItem",
				position: 2,
				name: label,
				item: `${aBaseUrl}${langPrefix}${pagePath}`,
			},
		],
	}
}

function courseListSchema(aLang: PageSeoLang, aBaseUrl: string): Record<string, unknown> {
	const provider = {
		"@type": "Organization",
		name: "CIL4Sys Engineering",
		url: aBaseUrl,
	}
	const courses =
		aLang === "fr"
			? [
					{
						"@type": "Course",
						name: "Introduction à SysML v2",
						description:
							"Journée d'introduction au langage SysML v2, ses concepts fondamentaux et ses apports pour la modélisation de systèmes complexes dans une démarche MBSE agile.",
						provider,
						url: `${aBaseUrl}/fr/formation/`,
					},
					{
						"@type": "Course",
						name: "Ingénierie système agile avec Sim4Sys",
						description:
							"Formation pratique de 3 jours : modélisation avec Sim4Sys Designer, tests sur Virtual Bench, génération automatique d'exigences et de documents.",
						provider,
						url: `${aBaseUrl}/fr/formation/`,
						timeRequired: "P3D",
					},
			  ]
			: [
					{
						"@type": "Course",
						name: "Introduction to SysML v2",
						description:
							"An introductory day on SysML v2 concepts and how they support modeling of complex systems in an agile MBSE approach.",
						provider,
						url: `${aBaseUrl}/formation/`,
					},
					{
						"@type": "Course",
						name: "Agile Systems Engineering with Sim4Sys",
						description:
							"Hands-on 3-day training: model with Sim4Sys Designer, test in Virtual Bench, and automatically generate requirements and documents from models.",
						provider,
						url: `${aBaseUrl}/formation/`,
						timeRequired: "P3D",
					},
			  ]
	return {
		"@context": "https://schema.org",
		"@type": "ItemList",
		itemListElement: courses.map((aCourse, anIndex) => ({
			"@type": "ListItem",
			position: anIndex + 1,
			item: aCourse,
		})),
	}
}

/**
 * Extra JSON-LD for GEO / entity clarity. FAQ text mirrors on-page topics (AEO; Google FAQ rich results limited).
 */
export function getGeoStructuredData(
	aPageKey: string,
	aLang: PageSeoLang,
	aSiteUrl: string
): Array<Record<string, unknown>> {
	if (process.env.GATSBY_STRAPI_PROJECT === "squaremiles") {
		return []
	}

	const baseUrl = aSiteUrl.replace(/\/$/, "")
	const someExtra: Array<Record<string, unknown>> = []

	const someFaqs = getPageFaqs(aPageKey, aLang)
	if (someFaqs.length > 0) {
		someExtra.push(faqPageSchema(someFaqs))
	}

	const breadcrumb = breadcrumbSchema(aPageKey, aLang, baseUrl)
	if (breadcrumb) {
		someExtra.push(breadcrumb)
	}

	if (aPageKey === "solutions" || aPageKey === "home") {
		someExtra.push(...softwareApplicationSchema(baseUrl))
	}

	if (aPageKey === "formation") {
		someExtra.push(courseListSchema(aLang, baseUrl))
	}

	if (aPageKey.startsWith("resources/")) {
		const slug = aPageKey.slice("resources/".length)
		const article = getResourceArticle(slug)
		if (article) {
			const langPrefix = aLang === "fr" ? "/fr" : ""
			someExtra.push({
				"@context": "https://schema.org",
				"@type": "Article",
				headline: article.h1[aLang],
				description: article.description[aLang],
				datePublished: article.datePublished,
				dateModified: article.dateModified,
				inLanguage: aLang === "fr" ? "fr-FR" : "en-US",
				author: { "@type": "Organization", name: "CIL4Sys Engineering", url: baseUrl },
				publisher: {
					"@type": "Organization",
					name: "CIL4Sys Engineering",
					logo: { "@type": "ImageObject", url: `${baseUrl}/logo.png` },
				},
				mainEntityOfPage: `${baseUrl}${langPrefix}/resources/${slug}/`,
				url: `${baseUrl}${langPrefix}/resources/${slug}/`,
			})
		}
	}

	if (aPageKey === "services" || aPageKey === "home") {
		someExtra.push({
			"@context": "https://schema.org",
			"@type": "ProfessionalService",
			name: "CIL4Sys Engineering",
			url: baseUrl,
			image: `${baseUrl}/og-image.jpg`,
			description:
				aLang === "fr"
					? "Services d'ingénierie système agile et MBSE : spécifications basées modèles, transformation digitale, outils et validation par simulation."
					: "Agile systems engineering and MBSE services: model-based specifications, digital transformation, tooling, and simulation-based validation.",
			areaServed: ["FR", "EU"],
			address: {
				"@type": "PostalAddress",
				streetAddress: "34 rue Godot de Mauroy",
				addressLocality: "Paris",
				postalCode: "75009",
				addressCountry: "FR",
			},
			sameAs: SAME_AS,
		})
	}

	return someExtra
}

export function getOrganizationSameAs(): Array<string> {
	return SAME_AS
}
